import _isEmpty from 'underscore/cjs/isEmpty.js'
import dayjs from 'dayjs'

import { useUtmsStore } from './utms'
import BillUploadService from '~/server/service/energy-bill-upload.service'

const billUploadService = new BillUploadService()
 
export const useEnergyStore = defineStore('energy', {
  state: () => ({
    guides: {
      url: null,
      initialPage: ''
    },
    form: {},
    products: [],
    currentRetailers: []
  }),

  getters: {
    productRequest () {
      const form = this.form

      if (_isEmpty(form)) {
        return {}
      }

      const formData = flattenObj(form, 1)

      const payload = {
        concession: false,
        customerId: '',
        eBilling: true,
        electricityBillAvailable: false,
        electricityBillDays: 90,
        electricityControlledLoadUsage1: 0,
        electricityControlledLoadUsage2: 0,
        electricityCurrentSpend: 0,
        electricityCurrentSpendInclGst: 0,
        electricityCurrentSupplier: '',
        electricityCurrentSupplierId: 0,
        electricityDistributorId: '',
        electricityMeterType: 'Single',
        electricityOffPeakUsage: 0,
        electricityPeakUsage: 0,
        electricityReferenceUsage: true,
        electricityShoulderUsage: 0,
        electricitySolarFeed: 0,
        gasBillAvailable: false,
        gasBillDays: 90,
        gasBillType: 'Winter',
        gasCurrentSpend: 0,
        gasCurrentSpendInclGst: 0,
        gasCurrentSupplier: '',
        gasCurrentSupplierId: 0,
        gasDistributorId: '',
        gasHeating: false,
        gasMainsConnected: false,
        gasOffPeakUsage: 0,
        gasPeakUsage: 9000,
        hideProductDetails: false,
        includeBundleRequired: true,
        isConnection: false,
        lifeSupport: false,
        noContract: false,
        numberOfPeople: 3,
        payBillsOntime: false,
        referenceBillFrequency: 'Quarterly',
        renewableEnergy: false,
        solarPanels: false,
        swimmingPool: false,
        underFloorHeating: false
      }

      return {
        ...payload,
        postcode: formData?.address?.postcode || formData?.postcode,
        fuelType: formData?.fuel_type,
        accountType: formData?.account_type
      }
    },

    leadParams () {
      const form = this.form

      const { enableMsat, enableBillUpload, enableUsage, enableCurrentRetailer } = useRuntimeConfig().public

      if (_isEmpty(form)) {
        return {}
      }

      const utmsStore = useUtmsStore()
      const formData = flattenObj(form, 1)

      let address = ''
      if (formData.account_type === 'SME') {
        address = [
          formData?.postcode?.suburb || undefined,
          formData?.postcode?.state || undefined,
          formData?.postcode?.postcode || undefined
        ].filter(item => item).join(', ').trim()
      } else {
        address = formData?.address?.addressline
      }
      const name = (() => {
        const _temp = getName(formData?.full_name)

        return {
          first_name: _temp.first_name,
          last_name: _temp.last_name,
          fullname: _temp.name
        }
      })()

      const obj = {
        ...name,
        email: formData?.email,
        phone: formData?.phone,

        fuel_type: formData?.fuel_type,
        is_moving: formData?.sales_type === 'Better Deal' ? 'no' : 'yes',
        moving_date: (formData?.sales_type === 'Better Deal' || !formData?.moving_date?.length) ? '' : dayjs(formData?.moving_date, 'DD/MM/YYYY').format('YYYY-MM-DD'),
        sales_type: formData?.sales_type,
        account_type: formData?.account_type?.toLowerCase() === 'sme'
          ? 'Business'
          : 'Residential',
        own_or_rent: formData?.property_type,
        brand: 'CompareClub',

        solar: false,
        life_support: false,

        address_unit_number: formData?.address?.unitNumber,
        address_street_number: formData?.address?.streetNumber,
        address_street_name: formData?.address?.streetName,
        address_suburb: formData?.postcode?.suburb || formData?.address?.suburb,
        state: formData?.postcode?.state || formData?.address?.state,
        postcode: formData?.postcode?.postcode || formData?.address?.postcode,

        business_name: formData?.business_name,

        conversion_name: formData?.conversion_name || '',

        form_page: import.meta.client && window.location.href,
        landing_page: utmsStore.GET_UTMS_LANDING_PAGE,
        referrer: import.meta.client && window.document.referrer,

        productRequest: this.productRequest,
        address
      }

      if (enableMsat) {
        obj.msat_uid = formData?.msat_uid || ''
      }

      if (enableBillUpload) {
        obj.bill_uid = formData?.bill_uid || ''
      }

      if (enableUsage) {
        obj.size = formData?.energy_usage_estimate || ''
      }
      if (enableCurrentRetailer) {
        obj.electricity_retailer = formData?.electricity_retailer || ''
        obj.gas_retailer = formData?.gas_retailer || ''        
      }

      return obj
    }
  },

  actions: {
    async createLead (payload) {
      const utmsStore = useUtmsStore()

      const partnerRule = await this.getPartner({
        state: payload?.state,
        connection_type: (payload?.account_type.toLowerCase() === 'sme' || payload?.account_type.toLowerCase() === 'business')
          ? 'BUSINESS'
          : 'RESIDENTIAL'
      })
        .then(res => res?.data?.partner_rule ?? 'cc_eng_internal')
        .catch(error => {
          throw new Error(error)
        })

      const response = await useFetch('/api/lead/create', {
        method: 'post',
        body: {
          vertical: 'Energy',
          ...utmsStore.utms,
          ...payload,
          partner_rule: partnerRule
        }
      }).then(res => {
        const temp = res?.data.value

        if (temp?.error) {
          throw temp?.error
        }

        const data = temp?.data
        return data?.lead_uuid ?? undefined
      }).catch(error => {
        throw error
      })

      return response
    },

    async getProducts () {
      await useFetch('/api/energy/products', {
        method: 'post',
        body: {
          ...this.productRequest
        }
      }).then(res => {
        if (!res?.data?.error) {
          this.products = res?.data?.data
          setLocal('products', JSON.stringify(res?.data?.data))
        }
      }).catch(error => {
        throw new Error(error)
      })
    },

    async getPartner (payload) {
      const response = await useFetch('/api/lead/partner', {
        method: 'get',
        params: {
          vertical: 'Energy',
          ...payload
        }
      }).then(res => res?.data.value)

      return response
    },

    async getMsatAddress () {
      const form = this.form

      if (_isEmpty(form)) {
        return {}
      }

      const formData = flattenObj(form, 1)
      const address = {
        house_number: formData?.address?.unitNumber || '',
        street_number: formData?.address?.streetNumber || '',
        street_name: formData?.address?.streetName || '',
        suburb: formData?.address?.suburb || '',
        state: formData?.address?.state || '',
        postcode: formData?.address?.postcode || ''
      }

      const response = await $fetch('/api/energy/msat-address', {
        method: 'POST',
        body: {
          ...address
        }
      }).then(res => res?.data)

      return response
    },

    async confirmMsatAddress () {
      const form = this.form

      if (_isEmpty(form)) {
        return {}
      }

      const formData = flattenObj(form, 1)

      const nmiData = {
        nmi: formData?.selected_nmi || '',
        msat_uid: formData?.msat_uid || ''
      }

      const response = await $fetch('/api/energy/confirm-msat-address', {
        method: 'POST',
        body: {
          ...nmiData

        }
      }).then(res => res?.data)

      return response
    },

    async getBillUploadStatus (bill_uid = '') {
      const form = this.form

      if (_isEmpty(form) && !bill_uid) {
        return {}
      }

      const formData = flattenObj(form, 1)

      const billUid = {
        bill_uid: bill_uid || formData?.bill_uid || ''
      }

      const response = await $fetch('/api/energy/bill-upload-status', {
        method: 'POST',
        body: {
          ...billUid

        }
      }).then(res => res?.data)
        .catch(() => ({
          status: 'Error'
        }))

      return response
    },

    async uploadBill (bill_file = '') {
      const form = this.form

      if (_isEmpty(form) && !bill_file) {
        return {}
      }

      const formData = new FormData()
      const funnelFormData = flattenObj(form, 1)
      formData.append('bill_file', bill_file || funnelFormData?.upload_bill)

      const response = await billUploadService.new(formData)
      return response
    },

    async getCurrentRetailers () {
      this.currentRetailers = await $fetch('/api/energy/current-retailers', {
        method: 'GET'
      }).then(res => res?.data)
        .catch(error => {
          throw new Error(error)
        })

    }
  }
})
